import { useEffect, useRef, type RefObject } from 'react';
import { type CustomAnalyticsEvent, trackAnalyticsCustomEvent } from '../tracking';

/**
 * Calls trackAnalyticsCustomEvent when the element becomes visible for the first time.
 *
 * Example usage:
 *
 * ```tsx
 * const ref = useTrackFirstView({ eventCategory: 'Component Visible', eventAction: 'View' });
 *
 * return <div ref={ref} />;
 * ```
 */
export function useTrackFirstView<T extends HTMLElement = HTMLElement>(
   eventData: CustomAnalyticsEvent | (() => CustomAnalyticsEvent)
): RefObject<T> {
   const ref = useRef<T | null>(null);
   const hasTrackedRef = useRef(false);

   // Keep the latest eventData in a stable ref so inline function values
   // don't cause the observer setup to re-run on every render.
   const eventDataRef = useRef<CustomAnalyticsEvent | (() => CustomAnalyticsEvent)>(eventData);
   useEffect(() => {
      eventDataRef.current = eventData;
   }, [eventData]);

   useEffect(() => {
      if (!ref.current || hasTrackedRef.current) return;

      const observer = new IntersectionObserver(
         entries => {
            for (const entry of entries) {
               if (!entry.isIntersecting) continue;
               hasTrackedRef.current = true;
               const ed = eventDataRef.current;
               trackAnalyticsCustomEvent({
                  ...(typeof ed === 'function' ? ed() : ed),
               });
               observer.disconnect();
               break;
            }
         },
         { threshold: 0 } // 0 means as soon as one pixel is visible
      );

      observer.observe(ref.current);
      return () => {
         observer.disconnect();
      };
   }, []);

   return ref as RefObject<T>;
}
